import { useApiQuery, type Api } from "../../../services/api";
import { useActiveLang } from "../../app/hooks/useActiveLang";

/**
 * One rail chip, INFERRED from the router and never redeclared. `label` is what
 * to draw, ALREADY resolved for `contentLang` by the endpoint: the curated label
 * of `domain/mesh/descriptor-labels.ts` where the descriptor has one, the NLM
 * name otherwise. It does not pass through `t()` and must not — `t()` keys are
 * static literals (`allowDynamicKeys: false`), and there are 31k descriptors.
 *
 * Inferred from the AGGREGATE endpoint, which is the narrower of the two shapes:
 * it serves `{descriptorUi, label}` where `:uid/rail-terms` also serves a `count`.
 * A per-uid term is assignable to this, and the extra field is one no client may
 * read — `count` orders one practice's rail server-side and anchors an
 * integration invariant there, and neither survives a union (`allPracticesRailTerms`
 * says why). Inferring from the wide shape would publish a number the aggregate
 * cannot honestly supply.
 */
export type PracticeRailTerm =
  Api["get"]["/webapp/private/practice-profiles/rail-terms"]["output"]["body"]["terms"][number];

/**
 * WHICH corpus is being read: one practice, or every practice held. The single
 * owner of that union — the page names its header dropdown with it, the feed
 * requests with it, and the rail's engagement events are keyed on it.
 *
 * A union rather than a nullable uid, because `null` is already spent three
 * times over on this surface — "the URL pinned nothing", and "do not fire" in
 * both hooks — and the aggregate is none of those.
 *
 * The two endpoints behind it are two endpoints and not one with an optional
 * uid: the aggregate is reader-scoped where `:uid` names a shared profile any
 * authenticated reader may read.
 */
export type PracticeRailCorpus =
  { kind: "all" } | { kind: "practice"; uid: string };

/**
 * A DISCRIMINATED UNION, because the three answers are not the same answer — the
 * same reason `PracticeFeedResult` is one next door.
 *
 * `ready` with no term is NOT an error and must never collapse into one: it means
 * the matched corpus over-represents no descriptor, which is a THIN CORPUS. That
 * is a fact about the literature and an input to the acquisition queue (§6 Gaps),
 * not a failure the reader can act on.
 */
export type PracticeRailData =
  | { status: "loading" }
  | { status: "ready"; terms: ReadonlyArray<PracticeRailTerm> }
  | { status: "unsupported" };

/**
 * The MeSH descriptors the read corpus is about — the rail's chips.
 *
 * NOT the leaves of the profile's Boolean: a Boolean is RECALL vocabulary,
 * deliberately redundant, and a `leafKey` carries no displayable label. These are
 * what the matched set is ABOUT, aggregated in SQL over `works.mesh_index`.
 *
 * `null` is a view not resolved yet (the catalogue has not landed), so there is
 * no corpus to ask about. Room mode is NOT `null`: the feed leaves the practice,
 * the rail does not, since its MeSH chips are what leads back out of the Room.
 * ONE parameter and not a corpus plus an `enabled` flag, because the two could
 * never legitimately disagree.
 *
 * The aggregate reads a DIFFERENT endpoint rather than the same one with no uid,
 * and its chips are the per-practice rails merged by best rank — never a tally
 * over the union, which would hand the whole rail to the widest practice.
 *
 * ── Why every error reads as `unsupported` ──────────────────────────────────
 * The endpoint 501s deliberately on an active profile whose Boolean has no SQL
 * reading, and that is the case this state was named for. Every OTHER failure (a
 * 500, a dropped connection) has the same correct rendering, because the rail and
 * the feed are two halves of the SAME compiled Boolean: the feed cannot be
 * healthy while the rail is not. So the rail leaves the screen and the feed says
 * what happened, once. Carrying the status through to render two different
 * silences would be a distinction with no consequence.
 */
export function usePracticeRailTerms(
  corpus: PracticeRailCorpus | null
): PracticeRailData {
  const { contentLang } = useActiveLang();

  // BOTH hooks are called on every render and at most one is enabled, which is
  // what the rules of hooks require and what `false` is for. They are separate
  // cache entries on purpose: switching between a practice and the aggregate is
  // switching corpus, so each keeps its own answer and its own five minutes.
  const allQuery = useApiQuery(
    "/webapp/private/practice-profiles/rail-terms",
    corpus === null || corpus.kind !== "all"
      ? false
      : { query: { language: contentLang }, staleTime: 1000 * 60 * 5 }
  );

  const oneQuery = useApiQuery(
    "/webapp/private/practice-profiles/:uid/rail-terms",
    corpus === null || corpus.kind !== "practice"
      ? false
      : {
          param: { uid: corpus.uid },
          query: { language: contentLang },
          // Matches the endpoint's own `private, max-age=300`: the terms are a
          // property of the PRACTICE, identical for everyone holding it. It is
          // what keeps a practice switch and a switch straight back from
          // re-evaluating the Boolean, which is the real cost here.
          staleTime: 1000 * 60 * 5,
        }
  );

  const query = corpus !== null && corpus.kind === "all" ? allQuery : oneQuery;

  switch (query.state) {
    // `corpus: null`, which is no resolved view yet. The caller that switched the
    // query off is the same one not rendering its result, so the honest report
    // is the one it already ignores: ready, holding nothing.
    case "idle":
      return { status: "ready", terms: [] };
    case "pending":
      return { status: "loading" };
    case "error":
      return { status: "unsupported" };
    case "success":
      return { status: "ready", terms: query.body.terms };
    default:
      throw new Error(query satisfies never);
  }
}
